import {
    AfterViewInit,
    Component,
    HostBinding,
    Injector,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import { Auth, User, getIdTokenResult } from '@angular/fire/auth';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ActionHeaderService,
    AumGlobalAction,
    AumicoCoreLayoutProvider,
    AumicoCoreProvider,
    ConfigurationsProvider,
    EMPTY_ARRAY_LENGTH,
    EqualityCheckWarningDialogActions,
    IAumGlobalActionEvent,
    INotificationData,
    LANGUAGES,
    ModalComponent,
    ModalData,
    ModalTitleIcons,
    NotificationType,
    SubNavigationService,
    UnsavedDataWarningDialogActions,
    WarningsDialogActions,
} from '@modeso/aumico-lib-core-fe';

import { AumicoUserClaims, AuthProvider, AuthService } from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { BehaviorSubject, Observable, Subject, from, of } from 'rxjs';
import { find, map, mergeMap, switchMap } from 'rxjs/operators';
import { AumicoAppLayoutService, IExtraActionFooterButton } from '../../../../aumico-layout/src/lib';
import { Constants } from '../../../../aumico-lib-core-fe/src/lib/constants/constants';
import { LocalStorageService } from '../../../../aumico-lib-core-fe/src/lib/services/localStorage.service';
import { BannersEnum } from '../../../../aumico-lib-users-fe/src/lib/enums/banners.enum';
import { environment } from '../../environments/environment';
import { NestedRouteLevels } from '../enums/nested-routing-levels.enum';
import type { ComponentCanDeactivate } from '../interfaces/ComponentCanDeactivate.interface';
import type { ConsistencyCheckResult } from '../interfaces/consistency-check-result';
import { ShouldNotProceedResult } from '../interfaces/should-not-proceed-result';

const debug = Debug('aumico:admin:BasePageComponent');
const LANGUAGE_SEGMENT_INDEX = 1;
const SUCCESS_NOTIFICATION_DISPLAY_TIME_IN_MILLI_SEC = 2000;

@UntilDestroy()
@Component({
    selector: 'base-add',
    template: `<ng-content></ng-content>`,
})
export class BasePageComponent implements ComponentCanDeactivate, OnInit, AfterViewInit, OnDestroy {
    @ViewChild('footerTemplate')
    protected footerTemplate: TemplateRef<any> | undefined;

    protected activatedRoute: ActivatedRoute;
    protected router: Router;
    protected domSanitizer: DomSanitizer;
    protected aumicoCoreLayoutProvider: AumicoCoreLayoutProvider;
    protected actionHeaderService: ActionHeaderService;
    protected localStorageService: LocalStorageService;
    protected i18n: I18NextService;
    savedSuccessfully = new Subject<boolean>();
    protected subNavigationService: SubNavigationService;
    protected aumicoCoreProvider: AumicoCoreProvider;
    protected readonly authProvider: AuthProvider;
    protected service: AumicoAppLayoutService;
    protected authService: AuthService;
    protected hideFooterOption: boolean;

    public disableProceedButton = true;

    protected auth: Auth;

    protected configurationsProvider: ConfigurationsProvider;
    protected supportedLocales: string[] = [];
    protected footerExtraButtonActions: IExtraActionFooterButton[] = [];
    protected actionsListener$: ActionsSubject;
    protected currentActiveUser: User;
    activeUserAumicoInternalId: string;
    protected activeUserAumicoInternalId$ = new BehaviorSubject<string | null>(null);
    protected dismissedBanners: BannersEnum[] = [];

    constructor(injector: Injector, public dialog?: MatDialog) {
        this.activatedRoute = injector.get(ActivatedRoute);
        this.domSanitizer = injector.get(DomSanitizer);
        this.aumicoCoreLayoutProvider = injector.get(AumicoCoreLayoutProvider);
        this.service = injector.get(AumicoAppLayoutService);
        this.actionHeaderService = injector.get(ActionHeaderService);
        this.router = injector.get(Router);
        this.localStorageService = injector.get(LocalStorageService);
        this.i18n = inject(I18NextService);
        this.hideFooterOption = false;
        this.subNavigationService = injector.get(SubNavigationService);
        this.aumicoCoreProvider = injector.get(AumicoCoreProvider);
        this.configurationsProvider = injector.get(ConfigurationsProvider);
        this.authProvider = injector.get(AuthProvider);
        this.auth = injector.get(Auth);
        this.authService = injector.get(AuthService);
        this.actionsListener$ = injector.get(ActionsSubject);

        this.footerExtraButtonActions = [
            {
                label: this.i18n.t('aumico_button_save') as string,
                disabled: true,
                action: () => this.save(),
            },
        ];
    }

    /**
     * This style is applied automatically because the grid which is applied cannot be bypassed
     * TODO: We need to find a better way to make the grid work.
     * It should be like a Subgrid, but unfortunately Chrome doesn't support sub-grids.
     */
    @HostBinding('style.grid-column') get gridColumn(): SafeStyle {
        return this.domSanitizer.bypassSecurityTrustStyle('1 / span 16');
    }

    ngAfterViewInit(): void {
        debug('send footer', this.footerTemplate);
        this.service.changeFooter(this.footerTemplate);
    }

    canDeactivate(): Observable<boolean> {

        if (this.isDataNewlyCreatedAndSaveable()) {
            // NEW LOGIC:
            // 1. Check for warnings/consistency first
            return this.warningsAndConsistencyCheckResult().pipe(
                mergeMap((canProceed) => {
                    if (canProceed) {
                        // 2. If warnings are passed, Save the data and wait for completion
                        return this.save().pipe(
                            mergeMap(() => this.isSavedSuccessfully$())
                        );
                    } else {
                        // 3. User cancelled or failed the warning check; cancel navigation
                        return of(false);
                    }
                })
            );
        } else {
            // Existing logic for non-new data
            return this.isDataChanged().pipe(
                mergeMap((isDataChanged) => {
                    if (isDataChanged) {
                        return this.unsavedChangesAndWarningsAndConsistencyCheckResult();
                    } else {
                        return this.warningsAndConsistencyCheckResult();
                    }
                }),
            );
        }
    }

    private unsavedChangesAndWarningsAndConsistencyCheckResult(): Observable<boolean> {
        return this.openUnsavedChangesDialog().pipe(
            mergeMap((unsavedChangesPopupResult) => {
                if (unsavedChangesPopupResult == null) {
                    return of(false);
                }
                switch (unsavedChangesPopupResult) {
                    case UnsavedDataWarningDialogActions.SAVE_AND_PROCEED:
                        // if save and proceed clicked then check if data is consistent
                        return this.save().pipe(mergeMap(() => this.isSavedSuccessfully$()));
                    case UnsavedDataWarningDialogActions.DISCARD_AND_PROCEED:
                        this.handleDiscardChanges();
                        return of(true);
                    case UnsavedDataWarningDialogActions.DELETE_AND_PROCEED:
                        // If delete and proceed clicked then wait for delete completion
                        return this.waitForDeleteProjectCompletion();
                }
            }),
            mergeMap((isSavedSuccessfully) => {
                if (isSavedSuccessfully) {
                    return this.warningsResult();
                }
                return of(false);
            }),
            mergeMap((isProceed) => {
                if (isProceed) {
                    return this.consistencyCheckResult();
                }
                return of(false);
            }),
        );
    }

    private warningsAndConsistencyCheckResult(): Observable<boolean> {
        return this.warningsResult().pipe(
            mergeMap((proceed) => {
                if (proceed) {
                    return this.consistencyCheckResult();
                }
                return of(false);
            }),
        );
    }

    private consistencyCheckResult(): Observable<boolean> {
        return this.isDataConsistent().pipe(
            mergeMap((consistencyCheckDialogResult) => {
                if (!consistencyCheckDialogResult.isDataConsistent) {
                    // if data is inconsistent the open the consistency checks dialog and return its result
                    return this.consistencyCheckDialogResult(consistencyCheckDialogResult.warnings);
                }

                return of(true);
            }),
        );
    }

    private warningsResult(): Observable<boolean> {
        return this.isShouldNotProceed().pipe(
            mergeMap((shouldNotProceedResult) => {
                if (shouldNotProceedResult.shouldNotProceed) {
                    return this.warningsDialogResult(shouldNotProceedResult.warnings);
                }

                return of(true);
            }),
        );
    }

    private warningsDialogResult(warnings: string[]): Observable<boolean> {
        return this.openWarningsDialog(warnings).pipe(
            mergeMap((consistencyCheckDialogResult) => {
                if (consistencyCheckDialogResult == null) {
                    return of(false);
                }
                switch (consistencyCheckDialogResult) {
                    case WarningsDialogActions.PROCEED:
                        return of(true);
                    case WarningsDialogActions.GO_BACK_AND_CORRECT_IT:
                        return of(false);
                }
            }),
        );
    }

    private consistencyCheckDialogResult(warnings: string[]): Observable<boolean> {
        return this.openConsistencyCheckDialog(warnings).pipe(
            mergeMap((consistencyCheckDialogResult) => {
                if (consistencyCheckDialogResult == null) {
                    return of(false);
                }
                switch (consistencyCheckDialogResult) {
                    case EqualityCheckWarningDialogActions.PROCEED:
                        // if proceed clicked then check if data is consistent
                        return of(true);
                    case EqualityCheckWarningDialogActions.GO_BACK_AND_CORRECT_IT:
                        return of(false);
                }
            }),
        );
    }

    protected save(): Observable<void> {
        return of(void 0);
    }

    protected handleDiscardChanges(): void {
        return;
    }

    protected isShouldNotProceed(): Observable<ShouldNotProceedResult> {
        return of({ shouldNotProceed: false, warnings: [] });
    }


    protected shouldShowDeleteProjectOption(): boolean {
        // This method should be overridden in the general information page
        return false;
    }

    /**
     * Wait for delete project completion before allowing navigation
     * Should be overridden in pages that support delete project functionality
     */
    protected waitForDeleteProjectCompletion(): Observable<boolean> {

        return of(true);
    }

    protected isDataNewlyCreatedAndSaveable(): boolean {
        return false;
    }

    protected isDataChanged(): Observable<boolean> {
        return of(false);
    }

    protected isDataConsistent(): Observable<ConsistencyCheckResult> {
        return of({ isDataConsistent: true, warnings: [] });
    }

    isSavedSuccessfully$(): Observable<boolean> {
        return this.savedSuccessfully.asObservable();
    }

    ngOnInit(): void {
        // Early synchronous language resolution for URL-based language
        // This ensures components get the correct language immediately
        this.performEarlyLanguageResolution();

        this.configurationsProvider.dispatchSupportedLocalesList();

        this.configurationsProvider
            .getSupportedLocales$()
            .pipe(
                find((locales) => locales.length !== EMPTY_ARRAY_LENGTH),
                map((locales) =>
                    locales.filter(
                        (locale) => locale.toLocaleLowerCase() !== LANGUAGES.SWISS_ITALIAN.toLocaleLowerCase(),
                    ),
                ),
                // as soon as we have our filtered locales, fetch a fresh ID token result
                switchMap((locales) =>
                    from(
                        this.auth.currentUser ? getIdTokenResult(this.auth.currentUser, false) : Promise.resolve(null),
                    ).pipe(
                        map((tokenResult) => ({
                            locales,
                            claims: tokenResult?.claims as AumicoUserClaims | null,
                        })),
                    ),
                ),
                untilDestroyed(this),
            )
            .subscribe(({ locales, claims }) => {
                this.supportedLocales = locales.map((l) => l.toLocaleLowerCase());

                if (claims) {
                    // we always get the freshest claims here
                    this.currentActiveUser = this.auth.currentUser;
                    this.activeUserAumicoInternalId = claims.internalId ?? this.currentActiveUser?.uid ?? '';
                    this.activeUserAumicoInternalId$.next(this.activeUserAumicoInternalId);
                    debug('Got custom internalId:', this.activeUserAumicoInternalId);
                }

                // Unified language resolution for both authenticated and non-authenticated users
                this.resolveAndSetLanguage(claims);
            });

        this.setDebuggingMode();
        this.actionHeaderService.onActionEvent$.pipe(untilDestroyed(this)).subscribe(this.onAction.bind(this));

        debug('Subscribe to actionHeaderService - on');

        this.authProvider
            .getCurrentUser$()
            .pipe(untilDestroyed(this))
            .subscribe((currentUser) => {
                if (currentUser) {
                    this.dismissedBanners = currentUser.dismissedBanners || [];
                }
            });

        this.authProvider.dispatchGetCurrentUser();
    }

    /**
     * Early synchronous language resolution to handle URL-based language immediately.
     * This prevents the race condition where components render before async language resolution completes.
     */
    private performEarlyLanguageResolution(): void {
        if (this.configurationsProvider.isStorybook()) {
            return;
        }

        const urlLanguage = this.getLanguageFromUrl();
        if (urlLanguage) {
            // Use the hardcoded known languages for early validation
            const knownLanguages = ['de-de', 'de-ch', 'en-us', 'fr-ch', 'it-ch', 'de', 'en', 'fr', 'it'];
            if (knownLanguages.includes(urlLanguage.toLowerCase())) {
                debug('Early language resolution: applying URL language', urlLanguage);
                this.saveAndApplyLanguage(urlLanguage, null);
                return;
            }
        }

        // If no valid URL language, apply default to prevent English fallback
        debug('Early language resolution: applying default language', environment.defaultLanguage);
        this.saveAndApplyLanguage(environment.defaultLanguage, null);
    }

    /**
     * Unified language resolution following priority order:
     * 1. URL parameter (explicit user choice)
     * 2. User preferences (if authenticated)
     * 3. Local storage (browser preference)
     * 4. Browser language detection
     * 5. Default language
     */
    private resolveAndSetLanguage(claims: AumicoUserClaims | null): void {
        if (this.configurationsProvider.isStorybook()) {
            return;
        }

        const urlLanguage = this.getLanguageFromUrl();
        const userLocale = this.getUserLocale();
        const localStorageLanguage = this.localStorageService.getCurrentLanguageWithoutFallback();
        const browserLanguage = this.localStorageService.getBrowserLanguage();
        const defaultLanguage = environment.defaultLanguage;

        debug('Language resolution inputs:', {
            urlLanguage,
            userLocale,
            localStorageLanguage,
            browserLanguage,
            defaultLanguage,
        });

        // Priority-based language resolution
        const resolvedLanguage = this.resolveLanguageByPriority([
            urlLanguage,
            userLocale,
            localStorageLanguage,
            browserLanguage,
            defaultLanguage,
        ]);

        debug('Resolved language:', resolvedLanguage);

        // Only update URL if it's different and invalid
        if (urlLanguage && !this.isLanguageSupported(urlLanguage)) {
            this.updateUrlLanguage(resolvedLanguage);
        } else if (!urlLanguage) {
            // Add language to URL if missing
            this.updateUrlLanguage(resolvedLanguage);
        }

        // Always save and apply the resolved language
        this.saveAndApplyLanguage(resolvedLanguage, claims);
    }

    private getLanguageFromUrl(): string | null {
        const urlSegments = this.router.url.split('/');
        const languageSegment = urlSegments[LANGUAGE_SEGMENT_INDEX];
        return languageSegment || null;
    }

    private getUserLocale(): string | null {
        // TODO: Implement async user locale retrieval from AuthProvider.getCurrentUser$()
        // The user's locale is stored in GetUserDto.locale property
        // For now, returning null so we fall back to local storage/browser language
        // When user changes language via changeLanguage(), it updates their locale on the server
        return null;
    }

    private resolveLanguageByPriority(languages: (string | null | undefined)[]): string {
        for (const lang of languages) {
            if (lang && this.isLanguageSupported(lang)) {
                return lang;
            }
            // Also check base language (e.g., 'de' from 'de-CH')
            if (lang && lang.includes('-')) {
                const baseLang = lang.split('-')[0];
                if (this.isLanguageSupported(baseLang)) {
                    return this.findMatchingLocale(baseLang) || baseLang;
                }
            }
        }
        return environment.defaultLanguage; // Fallback
    }

    private isLanguageSupported(language: string): boolean {
        if (!language) return false;

        const lowerLang = language.toLowerCase();

        // Check exact match
        if (this.supportedLocales.includes(lowerLang)) {
            return true;
        }

        // Check base language match (e.g., 'de' matches 'de-ch')
        const baseLang = lowerLang.split('-')[0];
        return this.supportedLocales.some((locale) => locale.split('-')[0] === baseLang);
    }

    private findMatchingLocale(baseLang: string): string | null {
        return this.supportedLocales.find((locale) => locale.split('-')[0] === baseLang.toLowerCase()) || null;
    }

    private updateUrlLanguage(language: string): void {
        const urlSegments = this.router.url.split('/');

        if (urlSegments[LANGUAGE_SEGMENT_INDEX] && this.isLanguageSupported(urlSegments[LANGUAGE_SEGMENT_INDEX])) {
            // Replace existing language
            urlSegments[LANGUAGE_SEGMENT_INDEX] = language;
        } else {
            // Insert language at the beginning
            urlSegments.splice(LANGUAGE_SEGMENT_INDEX, 0, language);
        }

        const newUrl = urlSegments.join('/');
        debug('Updating URL to:', newUrl);
        // Use navigateByUrl (not navigate([newUrl])) so the query string is parsed instead of being
        // treated as part of a single path segment. navigate([...]) would percent-encode the '?' and
        // double-encode an already-encoded query value (e.g. backTarget), producing a broken segment
        // like "report/general%3FbackTarget%3D%252F..." that no route matches -> page not found.
        this.router.navigateByUrl(newUrl, { replaceUrl: true }); // Use replaceUrl to avoid history pollution
    }

    private saveAndApplyLanguage(language: string, claims: AumicoUserClaims | null): void {
        debug('Applying language:', language);

        // Save to local storage
        this.localStorageService.setCurrentLanguage(language);

        // Apply to i18n
        this.i18n.changeLanguage(language);

        // Note: User language is stored in the user.locale property on the backend
        // No need to store it separately in local storage preferences
    }

    private loadLocalizationUsingLanguageParam(): void {
        // This method is deprecated - use resolveAndSetLanguage instead
        debug('loadLocalizationUsingLanguageParam is deprecated, using unified language resolution');
        this.resolveAndSetLanguage(null);
    }

    private appendCurrentLanguageOnURL(): void {
        // This method is deprecated - use resolveAndSetLanguage instead
        debug('appendCurrentLanguageOnURL is deprecated, using unified language resolution');
        this.resolveAndSetLanguage(null);
    }

    private setDebuggingMode(): void {
        localStorage['debug'] = environment.enableDebugging;
    }

    private isLanguageAvailable(languageFromURL: string): boolean {
        // Use the new unified method
        return this.isLanguageSupported(languageFromURL);
    }

    protected saveCurrentLanguage(language: string): void {
        debug('current language is ' + language);
        //if (this.isLanguageAvailable(language)) {
        debug('base..., change *** language to: ', language);
        this.localStorageService.setCurrentLanguage(language);
        this.i18n.changeLanguage(language);
        //}
    }

    /**
     * Public method to change language manually (e.g., from language selector)
     */
    public changeLanguage(newLanguage: string): void {
        if (this.isLanguageSupported(newLanguage)) {
            this.updateUrlLanguage(newLanguage);
            this.saveAndApplyLanguage(newLanguage, null);

            // Update user's locale if authenticated
            if (this.currentActiveUser) {
                this.updateUserLocale(newLanguage);
            }
        } else {
            debug('Attempted to change to unsupported language:', newLanguage);
        }
    }

    private updateUserLocale(newLanguage: string): void {
        // Get current user data and update only the locale
        this.authProvider
            .getCurrentUser$()
            .pipe(untilDestroyed(this))
            .subscribe((currentUser) => {
                if (currentUser) {
                    const updateData: any = {
                        firstName: currentUser.firstName,
                        lastName: currentUser.lastName,
                        countryISO31661Alpha2Code: currentUser.countryISO31661Alpha2Code,
                        locale: newLanguage,
                        // Keep all other existing properties
                        ...(currentUser.title && { title: currentUser.title }),
                        ...(currentUser.phoneNumber && { phoneNumber: currentUser.phoneNumber }),
                        ...(currentUser.photoURL && { photoURL: currentUser.photoURL }),
                        ...(currentUser.subscribeMarketing !== undefined && {
                            subscribeMarketing: currentUser.subscribeMarketing,
                        }),
                        ...(currentUser.preferences && { preferences: currentUser.preferences }),
                        ...(currentUser.dismissedBanners && { dismissedBanners: currentUser.dismissedBanners }),
                    };

                    this.authProvider.dispatchUpdateCurrentUser(updateData);
                    debug('Updated user locale to:', newLanguage);
                }
            });
    }

    protected getCurrentLanguageWithFallBack(): LANGUAGES {
        // First check if there's a valid language in the URL
        const urlLanguage = this.getLanguageFromUrl();
        if (urlLanguage) {
            // If supportedLocales isn't loaded yet, do a basic check against known languages
            if (this.supportedLocales.length === 0) {
                // Basic validation against actual supported languages during initial load
                const knownLanguages = ['de-de', 'de-ch', 'en-us', 'fr-ch', 'it-ch', 'de', 'en', 'fr', 'it'];
                if (knownLanguages.includes(urlLanguage.toLowerCase())) {
                    return urlLanguage as LANGUAGES;
                }
            } else if (this.isLanguageSupported(urlLanguage)) {
                return urlLanguage as LANGUAGES;
            }
        }

        // Fall back to localStorage/default
        return this.localStorageService.getCurrentLanguage();
    }

    protected getActiveRoute(): ActivatedRoute {
        return this.activatedRoute;
    }

    /** In-app return target carried through login/2FA (REV-21656). Internal paths only — never an open redirect. */
    protected safeReturnUrl(): string | null {
        const raw = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');
        if (raw != null && raw.startsWith('/') && !raw.startsWith('//')) {
            return raw;
        }
        return null;
    }

    protected replaceLanguage(dynamicUrl: string, language: string): string {
        return dynamicUrl.split(':language').join(language);
    }

    /*** ActionHeaderService END ***/
    protected onAction(event: IAumGlobalActionEvent): void {
        debug('actionHeaderService.onActionEvent$', event);

        if (event != null && event.action === AumGlobalAction.GOBACK) {
            const currentRouteSegments = this.router.url.split('/');
            if (currentRouteSegments.length === NestedRouteLevels.FIVE) {
                this.router.navigate(['..'], { relativeTo: this.activatedRoute });
                return;
            }

            if (currentRouteSegments.length === NestedRouteLevels.SIX) {
                this.router.navigate(['../..'], { relativeTo: this.activatedRoute });
                return;
            }

            if (currentRouteSegments.length === NestedRouteLevels.SEVEN) {
                this.router.navigate(['../../..'], { relativeTo: this.activatedRoute });
                return;
            }
        }
    }
    /*** ActionHeaderService END ***/

    protected openConsistencyCheckDialog(
        warnings: string[],
    ): Observable<EqualityCheckWarningDialogActions | undefined> {
        if (this.dialog == null) {
            throw new Error(`dialog is undefined`);
        }

        return this.dialog
            .open<ModalComponent, ModalData, EqualityCheckWarningDialogActions>(ModalComponent, {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.DEFAULT,
                    footerButtons: [
                        {
                            localizationKey: 'aumico_reports_equality_check_dialog_proceed',
                            action: EqualityCheckWarningDialogActions.PROCEED,
                            disabled: false,
                            buttonColor: 'primary',
                        },
                        {
                            localizationKey: 'aumico_reports_equality_check_dialog_go_back_and_correct_it',
                            action: EqualityCheckWarningDialogActions.GO_BACK_AND_CORRECT_IT,
                            buttonColor: 'basic',
                        },
                    ],
                    content: {
                        text: warnings,
                        icon: 'aumico-reports-icon-lost-changes',
                    },
                },
            })
            .afterClosed();
    }

    protected openWarningsDialog(warnings: string[]): Observable<WarningsDialogActions | undefined> {
        if (this.dialog == null) {
            throw new Error(`dialog is undefined`);
        }

        return this.dialog
            .open<ModalComponent, ModalData, WarningsDialogActions>(ModalComponent, {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.DEFAULT,
                    footerButtons: [
                        {
                            localizationKey: 'aumico_reports_equality_check_dialog_proceed',
                            action: WarningsDialogActions.PROCEED,
                            disabled: false,
                            buttonColor: 'primary',
                        },
                        {
                            localizationKey: 'aumico_reports_equality_check_dialog_go_back_and_correct_it',
                            action: WarningsDialogActions.GO_BACK_AND_CORRECT_IT,
                            buttonColor: 'basic',
                        },
                    ],
                    content: {
                        text: warnings,
                        icon: 'aumico-reports-icon-lost-changes',
                    },
                },
            })
            .afterClosed();
    }

    protected openUnsavedChangesDialog(): Observable<UnsavedDataWarningDialogActions | undefined> {
        if (this.dialog == null) {
            throw new Error(`dialog is undefined`);
        }

        // Determine the correct localization key for the discard button
        const shouldShowDeleteOption = this.shouldShowDeleteProjectOption();
        const discardLocalizationKey = shouldShowDeleteOption
            ? 'aumico_reports_unsaved_changes_warning_dialog_discard_and_delete_project'
            : 'aumico_reports_unsaved_changes_warning_dialog_discard';

        return this.dialog
            .open<ModalComponent, ModalData, UnsavedDataWarningDialogActions>(ModalComponent, {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.DEFAULT,
                    footerButtons: [
                        {
                            localizationKey: 'aumico_reports_unsaved_changes_warning_dialog_save',
                            action: UnsavedDataWarningDialogActions.SAVE_AND_PROCEED,
                            buttonColor: 'primary',
                            disabled: this.disableProceedButton,
                        },
                        {
                            localizationKey: discardLocalizationKey,
                            action: shouldShowDeleteOption ? UnsavedDataWarningDialogActions.DELETE_AND_PROCEED : UnsavedDataWarningDialogActions.DISCARD_AND_PROCEED,
                            buttonColor: 'basic',
                        },
                    ],
                    content: {
                        text: shouldShowDeleteOption ? ['aumico_reports_unsaved_changes_and_delete_project_warning_dialog_content'] : ['aumico_reports_unsaved_changes_warning_dialog_content'],
                        icon: 'aumico-reports-icon-lost-changes',
                    },
                },
            })
            .afterClosed()
            .pipe(
                map((result) => {
                    return result;
                }),
            );
    }

    protected fireErrorMessage(messageKey?: string, titleKey?: string): void {
        const errorDescriptionLocalizationKey = messageKey ?? Constants.ERROR_DIALOG.SOMETHING_WENT_WRONG;

        this.aumicoCoreProvider.dispatchAddNotification(
            titleKey,
            errorDescriptionLocalizationKey,
            NotificationType.ERROR,
            3500, // 2.5 seconds
        );
    }

    protected fireSuccessNotification(notificationData?: INotificationData): void {
        if (notificationData == null) {
            notificationData = {
                descriptionKey: 'aumico_report_save_success_notification_description',
            };
        }

        this.aumicoCoreProvider.dispatchAddNotification(
            notificationData.titleKey,
            notificationData.descriptionKey,
            NotificationType.SUCCESS,
            SUCCESS_NOTIFICATION_DISPLAY_TIME_IN_MILLI_SEC,
        );
    }

    ngOnDestroy(): void {
        debug('reset footer', undefined);
        this.service.changeFooter(null);
    }
}
